/* eslint-disable no-underscore-dangle */
import { createSelector, createStructuredSelector } from "reselect"
import {
    downloadCenterPaths,
    outboundTextingPaths,
    profileEndpointFromDataType,
    profileUrlFromTypeAndId,
} from "imports/desktopPaths"
import { sheetsTooltips } from "sheets/components/helpTexts"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { selectLedgerSettingsId } from "shared/pac/selectors"
import * as userdataSelectors from "shared/userdata/userdata-selectors"
import classNames from "classnames"
import { getTransactionPath, getFilingDetailPath } from "pac/helperFunctions"
import { generateStaticUrl } from "shared/imports/sharedHelperFunctions"

const { TransactionType } = DjangIO.app.ledger.types

const selectDataType = (_, props) => props.dataType
const selectDatum = (_, props) => props.datum
const selectDatumUrlOverride = (_, props) => props.datumUrlOverride
const selectAllowDatumLink = (_, props) => props.allowDatumLink
const selectIsPublicSheet = (_, props) => props.isPublicSheet
const selectIsResourceTable = (_, props) => props.isResourceTable
const selectDisableDatumFilter = (_, props) => props.disableDatumFilter
const selectResourceTableDatumTooltipText = (_, props) => props.resourceTableDatumTooltipText
export const selectResourceTableDatumSearchTrigger = (_, props) => props.resourceTableDatumSearchTrigger
const selectDisableRowLink = (_, props) => props.disableRowLink

export const makeDatumSelectorJS = (value, defaultVal) =>
    createSelector(selectDatum, (datum) => (datum && datum.get(value)) || defaultVal)

export const makeInteractionCacheJSONSelector = (value, defaultVal) =>
    createSelector(selectDatum, (datum) => (datum && datum.getIn(["cache_json", value])) || defaultVal)

export const selectId = makeDatumSelectorJS("id")

export const selectDLCMostRecentProfileId = makeDatumSelectorJS("most_recent_profile_id")

// this selector returns the TransactionType of the Transaction for Transaction Sheets
export const selectTransactionType = makeDatumSelectorJS("transaction_type")
export const selectPersonId = makeDatumSelectorJS("person_id")
export const selectStafferId = makeDatumSelectorJS("staffer_id")
export const selectSupporterId = makeDatumSelectorJS("supporter_id")
export const selectPersonType = makeDatumSelectorJS("person_type")
export const selectMostRecentParty = makeDatumSelectorJS("most_recent_party")
export const selectImageUrl = makeDatumSelectorJS("image_url")
export const selectName = makeDatumSelectorJS("name")
export const selectTitle = makeDatumSelectorJS("title")
export const selectQuestion = makeDatumSelectorJS("question")
export const selectPerson = makeDatumSelectorJS("person")
export const selectSupporter = makeDatumSelectorJS("supporter")
export const selectNoteType = makeDatumSelectorJS("note_type")
export const selectInteractionType = makeInteractionCacheJSONSelector("interaction_type")
export const selectUserId = makeDatumSelectorJS("user_id")
export const selectIsDraft = makeDatumSelectorJS("is_draft")
export const selectDatumDataType = makeDatumSelectorJS("data_type")
export const selectCurrentStatus = makeDatumSelectorJS("current_status")
export const selectReferenceTransaction = makeDatumSelectorJS("reference_transaction")
export const selectFilingsFormType = makeDatumSelectorJS("form_type")
export const selectFilingsId = makeDatumSelectorJS("id")

/**
 * @returns Boolean - whether not an image url exists
 */
export const selectContainsImageUrl = createSelector(selectImageUrl, (imageUrl) => Boolean(imageUrl))

/**
 * Generates a static URL for the component
 */
export const selectStaticImageUrl = createSelector(selectImageUrl, (imageUrl) => generateStaticUrl(imageUrl))

/**
 * @returns Boolean - whether or not we want display a Press image (either icon or photo)
 */
export const selectShouldShowPersonImage = createSelector(selectIsResourceTable, (isResourceTable) => !isResourceTable)

export const selectBuildUrl = createSelector(selectDataType, selectId, (dataType, id) =>
    dataType ? profileUrlFromTypeAndId(dataType, id) : "",
)

export const selectSendEmailLink = createSelector(
    selectPersonId,
    selectPersonType,
    selectStafferId,
    selectSupporterId,
    (personId, personType, stafferId, supporterId) => {
        if (personId) {
            const qdt =
                personType === DjangIO.app.person.types.PersonType.press_contact.value
                    ? DjangIO.app.models.QuorumDataType.press_contact.value
                    : DjangIO.app.models.QuorumDataType.person.value
            return profileUrlFromTypeAndId(qdt, personId)
        } else if (stafferId) {
            return profileUrlFromTypeAndId(DjangIO.app.models.QuorumDataType.person.value, stafferId)
        } else if (supporterId) {
            return profileUrlFromTypeAndId(DjangIO.app.models.QuorumDataType.supporter.value, supporterId)
        } else {
            return ""
        }
    },
)

export const selectSMSMessageLink = createSelector(selectSupporterId, (supporterId) =>
    supporterId ? profileUrlFromTypeAndId(DjangIO.app.models.QuorumDataType.supporter.value, supporterId) : "",
)

export const selectCustomEventParticipantLink = createSelector(
    selectPerson,
    selectPersonType,
    selectSupporter,
    (person, personType, supporter) => {
        if (person) {
            const qdt =
                personType === DjangIO.app.person.types.PersonType.press_contact.value
                    ? DjangIO.app.models.QuorumDataType.press_contact.value
                    : DjangIO.app.models.QuorumDataType.person.value
            return profileUrlFromTypeAndId(qdt, DjangIO.app.person.models.NewPerson.idFromResourceUri(person))
        } else if (supporter) {
            return profileUrlFromTypeAndId(
                DjangIO.app.models.QuorumDataType.supporter.value,
                DjangIO.app.grassroots.models.NewSupporter.idFromResourceUri(supporter),
            )
        }
        return ""
    },
)

export const selectNoteLink = createSelector(
    selectNoteType,
    selectInteractionType,
    selectId,
    selectUserId,
    userdataSelectors.selectUserId,
    userdataSelectors.selectIsOrgAdmin,
    userdataSelectors.selectShouldLimitEditingNotesToAdminsAndCreators,
    (noteType, interactionType, id, noteUserId, userId, isAdmin, shouldLimit) => {
        const interactionTypeClass = DjangIO.app.userdata.types.InteractionType
        const donationTypes = [
            interactionTypeClass.donation_given.value,
            interactionTypeClass.donation_received.value,
            interactionTypeClass.donation_rejected.value,
            interactionTypeClass.intended_donation.value,
            interactionTypeClass.sponsorship.value,
            interactionTypeClass.grant.value,
        ]
        const searchLink = `/search/notes/?id__in=${id}`
        // when ff_view_only_note is enabled, clicking on log_interaction would be view only profile
        // which is fine to navigate to
        if (shouldLimit && !isAdmin && noteUserId !== userId && !isFeatureEnabled("ff_view_only_note")) {
            return searchLink
        }
        switch (noteType) {
            case DjangIO.app.userdata.types.NoteType.interaction.value:
                if (interactionType && donationTypes.includes(interactionType)) {
                    return `/log_donation/${id}`
                }
                return `/log_interaction/${id}`
            case DjangIO.app.userdata.types.NoteType.relationship.value:
                return `/log_relationship/${id}`
            case DjangIO.app.userdata.types.NoteType.comment.value:
                return `/log_comment/${id}`
            default:
                return searchLink
        }
    },
)

export const selectBulkSMSLink = createSelector(selectCurrentStatus, selectId, (currentStatus, id) =>
    currentStatus === DjangIO.app.texting.types.TextDeliveryStatus.draft.value
        ? `${outboundTextingPaths.textingCompose.replace(":id?/", id)}/`
        : `${outboundTextingPaths.textingDetails}${id}`,
)

/**
 * This selector returns the path for a Transaction row to the appropriate form...
 * @name selectTransactionPath
 * @function
 * @param {Number} argument - The type of Transaction - whether a Receipt or Disbursement
 * @param {Number} ledgerSettingsId - For TransactionResourceTables, The PAC id that the user is currently viewing. Pulled from props.params
 * @param {Number} id - the id of the datum
 * @returns {Type} - Something
 */
export const selectTransactionPath = createSelector(
    selectTransactionType,
    selectLedgerSettingsId,
    selectId,
    (transactionType, ledgerSettingsId, id) =>
        getTransactionPath({
            id,
            ledgerSettingsId,
            transactionType,
        }),
)

/**
 * This selector returns the path for a Filing row to the appropriate form...
 * @name selectFilingsPath
 * @function
 * @param {Number} formType - The type of Filing - (form 3x, form 1)
 * @param {Number} filingsId - the id of the pacfile
 * @param {Number} ledgerSettingsId - id of the current ledger settings
 */
export const selectFilingsPath = createSelector(selectDatum, (datum) => {
    const ledgerSettingsId = DjangIO.app.ledger.models.LedgerSettings.idFromResourceUri(datum.get("ledger_settings"))
    const filingId = datum.get("id")
    const formType = datum.get("form_type")
    const regionId = datum.get("region")
    return getFilingDetailPath(ledgerSettingsId, filingId, formType, regionId)
})

/**
 * This selector returns the path that should be navigated to when the user hovers over a cell in Sheet
 * @name selectCellLink
 * @function
 * @param {Number} dataType - The dataType of the Sheet or Resource Table
 * @param {Number} datumDataType - the dataType of the row of the sheet
 * @param {String} datumUrlOverride - a prop provided via the ResourceTableContainer
 * to override the first portion of the path.
 * @param {String} id - The id of the datum
 * @param {Number} isDraft - Whether or not this row represents a Draft BulkEmail
 * @param {Number} sendeMailLink - The path to segue to for SendEmail rows
 * @param {Number} customEventParticipantLink - The path to segue to for CustomEvent rows
 * @param {Number} noteLink - The path to segue to for note rows
 * @param {Number} buildUrl - The path to segue to in the default case if no other cases are relevant
 * @returns {String} - a Path to link to in resource table cells
 */
export const selectCellLink = createSelector(
    selectDataType,
    selectDatumDataType,
    selectDatumUrlOverride,
    selectId,
    selectIsDraft,
    selectSendEmailLink,
    selectCustomEventParticipantLink,
    selectNoteLink,
    selectBuildUrl,
    selectTransactionPath,
    selectFilingsPath,
    selectSMSMessageLink,
    selectBulkSMSLink,
    selectDisableRowLink,
    selectDLCMostRecentProfileId,
    (
        dataType,
        datumDataType,
        datumUrlOverride,
        id,
        isDraft,
        sendEmailLink,
        customEventParticipantLink,
        noteLink,
        buildUrl,

        transactionPath,
        filingsPath,
        smsMessageLink,
        bulkSMSLink,
        disableRowLink,
        mostRecentProfileId,
    ) => {
        if (datumUrlOverride) {
            return `${datumUrlOverride}/${id}/`
        } else if (disableRowLink) {
            return undefined
        }

        switch (DjangIO.app.models.QuorumDataType.by_value(dataType).key) {
            case DjangIO.app.models.QuorumDataType.bulk_email.key:
                return isDraft ? `/outbox/compose/${id}/` : `/outbox/details/${id}/`
            case DjangIO.app.models.QuorumDataType.bulk_sms.key:
                return bulkSMSLink
            case DjangIO.app.models.QuorumDataType.send_email.key:
                return sendEmailLink
            case DjangIO.app.models.QuorumDataType.custom_event_participant.key:
                return customEventParticipantLink
            case DjangIO.app.models.QuorumDataType.note.key:
                return noteLink
            case DjangIO.app.models.QuorumDataType.send_sms.key:
                return smsMessageLink
            case DjangIO.app.models.QuorumDataType.new_download_template.key:
                return (
                    `${downloadCenterPaths.profileDownloadCenterPrefix}${datumDataType}/${id}/` +
                    `${
                        mostRecentProfileId && datumDataType !== DjangIO.app.models.QuorumDataType.blank_download.value
                            ? `${mostRecentProfileId}/`
                            : ""
                    }` +
                    `${datumDataType === DjangIO.app.models.QuorumDataType.blank_download.value ? `${id}/` : ""}`
                )

            // The transaction table requires the ledger_settings id to view Transactions
            case DjangIO.app.models.QuorumDataType.transaction.key:
                return transactionPath
            case DjangIO.app.models.QuorumDataType.pac_filings.key:
                return filingsPath
            case DjangIO.app.models.QuorumDataType.user.key:
                return `/settings/my_information/user/${id}/`

            default:
                return profileEndpointFromDataType(dataType) ? buildUrl : undefined
        }
    },
)

export const selectShouldRenderLink = createSelector(
    selectIsPublicSheet,
    selectAllowDatumLink,
    (isPublicSheet, allowDatumLink) => !isPublicSheet && allowDatumLink,
)

export const selectDatumCellClassNames = createSelector(selectDisableRowLink, (disableRowLink) =>
    classNames({ "datum-link": !disableRowLink }),
)

export const selectIsHomepageDashboard = createSelector(
    selectId,
    userdataSelectors.selectHomepageDashboardId,
    (id, homepageDashboardId) => id === parseInt(homepageDashboardId),
)

/** @type {(state?: any, props?: any) => any} */
export const datumCellConnection = createStructuredSelector({
    id: selectId,
    shouldRenderLink: selectShouldRenderLink,
    datumCellClassNames: selectDatumCellClassNames,
    cellLink: selectCellLink,
})

export const selectEntityAssociatedWithTransaction = makeDatumSelectorJS("entity_associated_with_transaction")

/** @type {(state?: any, props?: any) => any} */
export const transactionDatumCellConnection = createStructuredSelector({
    ...datumCellConnection,
    entityAssociatedWithTransaction: selectEntityAssociatedWithTransaction,
    isAncestorChild: selectReferenceTransaction,
})

/** @type {(state?: any, props?: any) => any} */
export const dashboardDatumCellConnection = createStructuredSelector({
    ...datumCellConnection,
    isHomepageDashboard: selectIsHomepageDashboard,
})

/** @type {(state?: any, props?: any) => any} */
export const pressImageConnection = createStructuredSelector({
    containsImageUrl: selectContainsImageUrl,
    imageUrl: selectStaticImageUrl,
})

/** @type {(state?: any, props?: any) => any} */
export const pressDatumConnection = createStructuredSelector({
    personName: selectName,
    shouldShowPersonImage: selectShouldShowPersonImage,
})
